"use client";

import { CodeBlock, CodeBlockContent } from "@repo/ui/components/ui/code-block";
import {
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceIcon,
  TraceLine,
  TraceTitle,
} from "@repo/ui/components/ui/trace";
import { CircleAlertIcon, CircleCheckIcon, LoaderCircleIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { ToolPart } from "../turn";

export type StepStatus = "running" | "error" | undefined;

/** The Chain of Thought pattern's step status: a call still running reads
 * "error" once the chat has stopped, since it will never finish. */
export function stepStatus(part: ToolPart, streaming: boolean): StepStatus {
  if (part.state === "done") return undefined;
  return part.state === "error" || !streaming ? "error" : "running";
}

export function StatusIcon({ status }: { status?: StepStatus }) {
  if (status === "running") {
    return <LoaderCircleIcon className="animate-spin" />;
  }

  return status === "error" ? <CircleAlertIcon /> : <CircleCheckIcon />;
}

export function Step({ title, status, children }: { title: string; status?: StepStatus; children?: ReactNode }) {
  const line = (
    <TraceLine status={status}>
      <TraceIcon>
        <StatusIcon status={status} />
      </TraceIcon>
      <TraceTitle>{title}</TraceTitle>
    </TraceLine>
  );

  if (!children) {
    return line;
  }

  return (
    <TraceCollapsible>
      <TraceCollapsibleTrigger>{line}</TraceCollapsibleTrigger>
      <TraceCollapsibleContent className="flex flex-col gap-2">{children}</TraceCollapsibleContent>
    </TraceCollapsible>
  );
}

const stringify = (value: unknown) => (typeof value === "string" ? value : JSON.stringify(value, null, 2));

export function StepCodeBlock({ value, destructive }: { value: unknown; destructive?: boolean }) {
  return (
    <CodeBlock size="sm" variant={destructive ? "destructive" : "default"}>
      <CodeBlockContent wrap className="max-h-64">
        {stringify(value)}
      </CodeBlockContent>
    </CodeBlock>
  );
}
